import type { FC } from 'react'

import CellphoneIcon from 'mdi-react/CellphoneIcon'

import { Translation } from '@/app/translations/Translation'
import DataPoints, { DataPoint } from '@/components/DataPoints'
import { FormattedNumberWithCurrency } from '@/components/FormattedNumberWithCurrency'
import { LocalDateTime } from '@/components/LocalDateTime'
import RouterLink from '@/components/RouterLink'
import type { AssetExtended } from '@/modules/admin/resources'
import { APP_ROUTES } from '@/util/app-routes'

export const DevicePolicyOrigin: FC<{ assetDetails: AssetExtended }> = ({ assetDetails }) => {
  const {
    devicePolicyOrderCoverAmount,
    devicePolicyOrderTotalAmount,
    devicePolicyOrderRemainderAmount,
    devicePolicyOrderCurrency,
    devicePolicyOrderDate,
    devicePolicyOrderId,
  } = assetDetails

  const coverAmountPercentage =
    devicePolicyOrderCoverAmount && devicePolicyOrderTotalAmount
      ? Math.round((devicePolicyOrderCoverAmount / devicePolicyOrderTotalAmount) * 100)
      : 0
  const remainderAmountPercentage =
    devicePolicyOrderRemainderAmount && devicePolicyOrderTotalAmount
      ? Math.round((devicePolicyOrderRemainderAmount / devicePolicyOrderTotalAmount) * 100)
      : 0

  if (!devicePolicyOrderId) {
    return null
  }

  return (
    <section className="shadow-basic m-2 flex flex-1 basis-[30rem] rounded-md bg-white p-4">
      <DataPoints>
        {' '}
        <h3>
          <Translation id="Order Details" />
        </h3>
        <DataPoint
          header={<Translation id="Order Id" />}
          value={
            <RouterLink
              to={APP_ROUTES.DEVICE_ORDER.replace(':id', devicePolicyOrderId.toString())}
              hasUnderline={false}
              className="order-id-link"
            >
              {devicePolicyOrderId}
              <CellphoneIcon className="mt-0.5 h-5 w-5 p-0.5 align-top" />
            </RouterLink>
          }
        />
        <DataPoint
          header={<Translation id="Ordered" />}
          value={<LocalDateTime date={devicePolicyOrderDate} />}
        />
        <DataPoint
          header={<Translation id="Covered Amount" />}
          value={
            <>
              <FormattedNumberWithCurrency
                amount={devicePolicyOrderCoverAmount}
                currency={devicePolicyOrderCurrency}
              />
              {devicePolicyOrderCoverAmount !== 0 && (
                <span className="percentage"> {`${coverAmountPercentage}%`}</span>
              )}
            </>
          }
        />
        <DataPoint
          header={<Translation id="Deductable Amount" />}
          value={
            <>
              <FormattedNumberWithCurrency
                amount={devicePolicyOrderRemainderAmount}
                currency={devicePolicyOrderCurrency}
              />
              {devicePolicyOrderRemainderAmount !== 0 && (
                <span className="percentage"> {`${remainderAmountPercentage}%`} </span>
              )}
            </>
          }
        />
        <DataPoint
          header={<Translation id="Total Cost" />}
          value={
            <FormattedNumberWithCurrency
              amount={devicePolicyOrderTotalAmount}
              currency={devicePolicyOrderCurrency}
            />
          }
        />
      </DataPoints>
    </section>
  )
}
